import axios from "axios";
import queryString from "qs";
import { API_BASE_URL, API_PREFIX } from "@/constants";
import { AdminAuthService } from "@/services/auth-service";
import { useAdminAuthStore } from "@/stores/admin-auth-store";

// Per-request opt-outs from the global redirect behavior. A page that wants
// to render its own error UI can pass these in the request config, e.g.:
//   apiClient.get("/projects/", { skipServerErrorRedirect: true })
// The interceptor below skips the corresponding window.location.replace
// so the caller's catch/allSettled branch can render inline.
declare module "axios" {
  export interface AxiosRequestConfig {
    skipServerErrorRedirect?: boolean;
    skipNotFoundRedirect?: boolean;
  }
}

// Server-error statuses handled by the dedicated error page.
const SERVER_ERROR_STATUSES = new Set([500, 502, 503]);

export const apiClient = axios.create({
  baseURL: `${API_BASE_URL}${API_PREFIX}`,
  headers: { "Content-Type": "application/json" },
  paramsSerializer: {
    serialize: (params) => {
      return queryString.stringify(params, {
        arrayFormat: "repeat",
        allowDots: true,
      });
    },
  },
});

let isRefreshing = false;
let refreshSubscribers: Array<(token: string) => void> = [];

const subscribeTokenRefresh = (callback: (token: string) => void) => {
  refreshSubscribers.push(callback);
};

const onRefreshed = (token: string) => {
  refreshSubscribers.forEach((cb) => cb(token));
  refreshSubscribers = [];
};

apiClient.interceptors.request.use((config) => {
  const token = localStorage.getItem("admin_access_token");
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

apiClient.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;

    if (error.response?.status === 401 && !originalRequest._retry) {
      if (originalRequest.url?.includes("/admins/auth/")) {
        return Promise.reject(error);
      }
      if (isRefreshing) {
        return new Promise((resolve) => {
          subscribeTokenRefresh((token) => {
            originalRequest.headers.Authorization = `Bearer ${token}`;
            resolve(apiClient(originalRequest));
          });
        });
      }

      originalRequest._retry = true;
      isRefreshing = true;

      try {
        const refreshToken = localStorage.getItem("admin_refresh_token");
        if (!refreshToken) throw new Error("No refresh token");

        const res = await AdminAuthService().adminRefreshToken(refreshToken);
        const tokens = res.data.data;
        if (!tokens) throw new Error("No tokens in response");

        localStorage.setItem("admin_access_token", tokens.access_token);
        localStorage.setItem("admin_refresh_token", tokens.refresh_token);
        useAdminAuthStore.getState().setTokens(tokens.access_token, tokens.refresh_token);

        onRefreshed(tokens.access_token);
        isRefreshing = false;

        originalRequest.headers.Authorization = `Bearer ${tokens.access_token}`;
        return apiClient(originalRequest);
      } catch (refreshError) {
        isRefreshing = false;
        refreshSubscribers = [];
        localStorage.removeItem("admin_access_token");
        localStorage.removeItem("admin_refresh_token");
        useAdminAuthStore.getState().logout();
        window.location.href = "/admin/login";
        return Promise.reject(refreshError);
      }
    }
    if (error.response?.status === 404 && !originalRequest.url?.includes("/admins/auth/")) {
      window.dispatchEvent(new CustomEvent("server:not-found"));
    }

    if ([500, 502, 503].includes(error.response?.status)) {
      if (!(originalRequest as any).skipGlobalError) {
        window.dispatchEvent(
          new CustomEvent("server:error", {
            detail: { status: error.response.status },
          }),
        );
      }
    }

    return Promise.reject(error);
  },
);
